import { useCallback, useEffect, useRef, useState, type DragEvent, type FormEvent } from "react";
import { get, patch, post, type Me, type Revision, type Trip } from "../../lib/api.ts";
import type { BoardRow } from "../../lib/board.ts";
import { isAdmin } from "../../lib/rights.ts";
import { formatBytes } from "../../lib/transfer.ts";
import type { UploadProgress } from "../../lib/upload.ts";
import { TagFolder } from "../media/TagsTab.tsx";
import { ConfirmDialog } from "../../ui/index.ts";
import { tabKeys, tabStop } from "../../ui/tabs.ts";
import { RevisionReview } from "./RevisionReview.tsx";
import { isUploadable, uploadVersion } from "./uploadVersion.ts";

const PHASE: Record<UploadProgress["phase"], string> = {
  hashing: "Reading your file",
  preparing: "Preparing",
  uploading: "Uploading",
  completing: "Finishing upload",
  verifying: "Checking the upload",
  publishing: "Publishing",
  done: "Done",
};

type Props = {
  trip: Trip;
  me: Me;
  row: BoardRow;
  onChanged: () => Promise<void>;
  /** The phone's way back to the board. */
  onLeave?: () => void;
  /**
   * Tells the host what a file dropped right now would do: a sentence while the pointer is over
   * this pane's version box, null when it is not. The page's own drop starts a new project; this
   * box is the one place that means something else, and the host's wash has to say so.
   */
  onAim?: (label: string | null) => void;
  /**
   * A file was dropped on the version box, whatever came of it. The box keeps the board from
   * hearing the drop, and a browser sends no drag-leave after one, so the host's wash would
   * otherwise stay up. Separate from `onAim(null)`, which also means "the pointer moved off the
   * box, still dragging".
   */
  onDropped?: () => void;
};

const carriesFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/**
 * One row of the board, opened: the brief, the footage it is about, the versions made from it and
 * the box the next version goes into.
 *
 * A task with no project yet has nothing to review, so it offers to start one; the project takes
 * the task's name and is created only when a file is ready to go into it. A project whose task
 * went private keeps its versions and loses its brief and folder: it has no task to show.
 */
export function BoardDetail(props: Props) {
  // Another row is another detail: no draft, selection or half-made upload carries across.
  const { row } = props;
  const rowKey = row.kind === "PROJECT" ? `p:${row.project.id}` : `t:${row.task.tagId}`;
  // The message lives out here, not in Detail: a failed upload refreshes the board, which turns a
  // task into its new project and remounts Detail, and a message that went with it is no message.
  // It belongs to the task, so it follows the task from "not started" to its project.
  const scope = row.task?.tagId ?? rowKey;
  const [held, setHeld] = useState<{ scope: string; message: string } | null>(null);
  return (
    <Detail
      key={rowKey}
      {...props}
      error={held?.scope === scope ? held.message : null}
      setError={(message) => setHeld(message === null ? null : { scope, message })}
    />
  );
}

function Detail({
  trip,
  me,
  row,
  onChanged,
  onLeave,
  onAim,
  onDropped,
  error,
  setError,
}: Props & { error: string | null; setError: (message: string | null) => void }) {
  const base = `/v1/trips/${trip.id}`;
  const project = row.kind === "PROJECT" ? row.project : null;
  const task = row.task;
  const tagId = row.kind === "TASK" ? row.task.tagId : (row.project.tagId ?? null);
  const canUpload = trip.roles.includes("EDITOR");
  // Two things to look at only once a project exists to look at: a task nobody has started has
  // nothing under Versions, and a tab leading to an empty panel is worse than no tab.
  const tabbed = project !== null && task !== null && tagId !== null;

  const [title, setTitle] = useState<string | null>(null); // non-null while renaming
  const [revs, setRevs] = useState<Revision[] | null>(null);
  // Which half of a brief-backed project is showing. Versions first: the cuts are what the
  // editor comes back to, and the footage is what they go looking for. A task nobody has started
  // has no versions to tab to, so it shows its footage outright.
  const [view, setView] = useState<"versions" | "media">("versions");
  const [selected, setSelected] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [resuming, setResuming] = useState(false);
  const [dropping, setDropping] = useState(false); // the confirm step before a project is dropped
  // A project made for a task whose upload then failed: retrying must not make a second one.
  const [startedId, setStartedId] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const uploading = progress !== null;

  const draft = project?.unfinishedDraft ?? null;
  const projectId = project?.id ?? null;
  const currentId = project?.current?.id ?? null;
  const versionCount = project?.versionCount ?? 0;
  // Closing the picker without a file means nobody is resuming anything.
  useEffect(() => {
    const el = picker.current;
    const stop = () => setResuming(false);
    el?.addEventListener("cancel", stop);
    return () => el?.removeEventListener("cancel", stop);
  }, []);
  // Only the latest read may land: a slow earlier one must not overwrite a newer list.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    if (!projectId) return;
    const seq = ++loadSeq.current;
    const r = await get<{ revisions: Revision[] }>(`${base}/deliverables/${projectId}/revisions`);
    if (seq === loadSeq.current) setRevs(r.revisions);
  }, [base, projectId]);
  useEffect(
    () => () => {
      loadSeq.current++;
    },
    [],
  );
  useEffect(() => {
    // The board moves on when a version lands or is decided; follow it.
    load().catch((e: Error) => setError(e.message));
  }, [load, currentId, versionCount]);

  const current = revs?.find((r) => r.isCurrent) ?? revs?.[revs.length - 1] ?? null;
  const sel = revs?.find((r) => r.id === selected) ?? current;

  // The server's rule for renaming, dropping and reopening: an admin, or whoever started the
  // project. Not whose version is newest (`isMine`), which only sorts the board.
  const mayChange = project !== null && (isAdmin(trip.roles) || project.createdBy === me.user.id);
  const canRename =
    project !== null &&
    mayChange &&
    project.status !== "APPROVED" &&
    project.status !== "DELIVERED";

  const dropped = project?.status === "ABANDONED";
  const canDrop = canRename && !dropped;
  const canReopen = mayChange && dropped;

  async function setAside(verb: "abandon" | "reopen") {
    if (!project) return;
    setError(null);
    try {
      await post(`${base}/deliverables/${project.id}/${verb}`, {});
      await onChanged();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function rename(e: FormEvent) {
    e.preventDefault();
    if (!project || title === null || !title.trim()) return;
    setError(null);
    try {
      await patch(`${base}/deliverables/${project.id}`, { title: title.trim() });
      setTitle(null);
      await onChanged();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function choose(next: File | null) {
    if (next && !isUploadable(next)) {
      setFile(null);
      setResuming(false);
      setError("Choose a video or an image file.");
      return;
    }
    setError(null);
    // A different file cannot continue that draft, and the server's refusal would read as nonsense.
    if (resuming && (!next || String(next.size) !== draft?.sizeBytes)) setResuming(false);
    setFile(next);
  }

  async function discard() {
    if (!project?.unfinishedDraft) return;
    setError(null);
    try {
      await post(
        `${base}/deliverables/${project.id}/revision-drafts/${project.unfinishedDraft.id}/cancel`,
        {},
      );
      setResuming(false);
      await onChanged();
    } catch (err) {
      setError((err as Error).message);
    }
  }

  /** The one way a file becomes a version here, whether it was chosen or dropped on the box. */
  async function send(file: File, resume: boolean) {
    setError(null);
    setProgress({ phase: "preparing", fraction: 0 });
    let madeHere = false;
    try {
      const existing = projectId ?? startedId;
      if (!existing && row.kind !== "TASK") return;
      await uploadVersion({
        tripId: trip.id,
        target: existing
          ? { deliverableId: existing }
          : { create: { title: row.task!.name, tagId: row.task!.tagId } },
        file,
        notes,
        onProgress: setProgress,
        onCreated: (id) => {
          setStartedId(id);
          madeHere = true;
        },
        ...(resume && project?.unfinishedDraft
          ? { resumeDraftId: project.unfinishedDraft.id }
          : {}),
      });
      setFile(null);
      setNotes("");
      setResuming(false);
      setSelected(null);
      await load().catch(() => {});
      await onChanged();
    } catch (err) {
      setError((err as Error).message);
      // The server now holds a project with no versions; let the board say so too.
      if (madeHere) await onChanged().catch(() => {});
    } finally {
      setProgress(null);
    }
  }

  function upload(e: FormEvent) {
    e.preventDefault();
    if (file) void send(file, resuming);
  }

  const aim =
    row.kind === "PROJECT"
      ? `Drop to add a version to “${row.project.title}”`
      : `Drop to start a project for “${row.task.name}”`;
  // Only the box claims a drop, and only the box says what it will do; stopping the event is what
  // keeps the page's own drop (a new project) from also firing.
  function aimed(e: DragEvent) {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    onAim?.(aim);
  }
  function left(e: DragEvent) {
    // Moving between the box's own children is not leaving it. Not stopped: the board decides
    // whether the pointer has left the page altogether.
    if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return;
    onAim?.(null);
  }
  function landed(e: DragEvent) {
    if (!carriesFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    onAim?.(null);
    onDropped?.();
    const next = e.dataTransfer.files[0];
    if (!next || uploading) return;
    if (!isUploadable(next)) {
      setError("Choose a video or an image file.");
      return;
    }
    // A different file cannot continue that draft, as when one is chosen.
    const resume = resuming && String(next.size) === draft?.sizeBytes;
    setResuming(resume);
    setFile(next);
    void send(next, resume);
  }

  const name = project ? project.title : row.kind === "TASK" ? row.task.name : "";

  return (
    <div className="stack board-detail">
      {onLeave && (
        <div className="row">
          <button type="button" className="button" onClick={onLeave}>
            Back to the board
          </button>
        </div>
      )}

      {title !== null ? (
        <form className="row wrap" onSubmit={(e) => void rename(e)}>
          <input
            className="grow"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            aria-label="Project name"
          />
          <button className="button primary" disabled={!title.trim()}>
            Save name
          </button>
          <button type="button" className="button" onClick={() => setTitle(null)}>
            Cancel
          </button>
        </form>
      ) : (
        <div className="row wrap">
          <h2 className="grow">{name}</h2>
          {canRename && (
            <button type="button" className="button" onClick={() => setTitle(name)}>
              Rename
            </button>
          )}
          {canDrop && (
            <button type="button" className="button" onClick={() => setDropping(true)}>
              Drop this project
            </button>
          )}
        </div>
      )}

      <ConfirmDialog
        open={dropping}
        title={`Drop “${name}”?`}
        body="It moves to Dropped and stops taking versions. Its versions are kept, and you can reopen it later."
        confirmLabel="Drop project"
        onConfirm={() => {
          setDropping(false);
          void setAside("abandon");
        }}
        onCancel={() => setDropping(false)}
      />

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {task && (
        <section className="card stack" aria-label="The brief">
          <h3>The brief</h3>
          {task.note ? <p>{task.note}</p> : <p className="muted">No note on this task.</p>}
          <p className="small muted">Set by {task.ownerName}</p>
        </section>
      )}

      {/* A project that answers an idea has two things to look at and no room for both: the
          footage the brief is about, and the cuts made from it. Tabs rather than a longer scroll,
          because they are read at different moments — gathering, then reviewing. */}
      {tabbed && (
        <div className="lib-tabs" role="tablist" aria-label="Project view" onKeyDown={tabKeys}>
          {(["versions", "media"] as const).map((t, i) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={view === t}
              tabIndex={tabStop(i, view === "versions" ? 0 : 1)}
              className={`lib-tab${view === t ? " on" : ""}`}
              onClick={() => setView(t)}
            >
              {t === "versions" ? "Versions" : "Media"}
            </button>
          ))}
        </div>
      )}

      {task && tagId && (!tabbed || view === "media") && (
        <section className="stack" aria-label="The footage">
          <TagFolder trip={trip} tag={null} tagId={tagId} onChanged={() => void onChanged()} />
        </section>
      )}

      {project && (!tabbed || view === "versions") && (
        <section className="stack" aria-label="Versions">
          <h3>Versions</h3>
          {revs === null ? (
            <p className="muted">Loading…</p>
          ) : revs.length === 0 ? (
            <p className="muted">No versions uploaded yet.</p>
          ) : (
            <div className="version-cards" role="tablist" aria-label="Versions">
              {revs.map((r) => {
                const last = r.decisions.at(-1);
                return (
                  <button
                    key={r.id}
                    role="tab"
                    aria-selected={r.id === sel?.id}
                    className="version-card"
                    onClick={() => setSelected(r.id)}
                  >
                    <strong>V{r.versionNumber}</strong>
                    <span className="small muted">
                      {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                    {last ? (
                      <span className={`pill ${last.decision === "APPROVE" ? "good" : "wait"}`}>
                        {last.decision === "APPROVE" ? "Approved" : "Changes requested"}
                      </span>
                    ) : (
                      <span className="pill">{r.isCurrent ? "Current" : "Superseded"}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          {sel && (
            <RevisionReview
              key={sel.id}
              trip={trip}
              me={me}
              revision={sel}
              taskOwnerId={task?.ownerId ?? null}
              // Deciding is not the editor's to do on their own cut — this board offers them no
              // approve and no request-changes, whatever role they also hold. The admin's page
              // is where a version is accepted.
              allowDecisions={false}
              onChanged={async () => {
                await load();
                await onChanged();
              }}
            />
          )}
        </section>
      )}

      {draft && canUpload && !dropped && (
        <section className="card stack" aria-label="Unfinished upload">
          <p>An upload of {formatBytes(Number(draft.sizeBytes))} was left unfinished.</p>
          {resuming && <p className="muted">Choose the same file again to carry on.</p>}
          <div className="row wrap">
            <button
              type="button"
              className="button"
              disabled={uploading}
              onClick={() => {
                setResuming(true);
                picker.current?.click();
              }}
            >
              Resume upload
            </button>
            <button
              type="button"
              className="button"
              disabled={uploading}
              onClick={() => void discard()}
            >
              Discard upload
            </button>
          </div>
        </section>
      )}

      {dropped && (
        <section className="card stack" aria-label="Dropped project">
          <p>This project was dropped, so it takes no new versions until it is reopened.</p>
          {canReopen && (
            <div className="row">
              <button type="button" className="button" onClick={() => void setAside("reopen")}>
                Reopen
              </button>
            </div>
          )}
        </section>
      )}

      {canUpload && !dropped && (
        <form
          onSubmit={upload}
          onDragOver={aimed}
          onDragLeave={left}
          onDrop={landed}
          className="stack card dropzone"
        >
          <h3>
            {row.kind === "TASK"
              ? `Start a project named “${row.task.name}”`
              : "Upload a new version"}
          </h3>
          <p className="muted">
            Edit in your own editing software, then upload the export here. If it is interrupted,
            choose the same file again to carry on.
          </p>
          <input
            ref={picker}
            type="file"
            accept="video/*,image/*"
            onChange={(e) => choose(e.target.files?.[0] ?? null)}
            disabled={uploading}
            aria-label="Video or image file"
          />
          {file && (
            <p className="muted">
              {file.name} · {formatBytes(file.size)}
            </p>
          )}
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What changed? (optional)"
            maxLength={4000}
            disabled={uploading}
            aria-label="What changed"
          />
          <button className="button primary" disabled={!file || uploading}>
            {row.kind === "TASK" ? "Start project and upload" : "Upload version"}
          </button>
          {progress && (
            <div role="status">
              <progress
                max={1}
                value={progress.phase === "verifying" ? undefined : progress.fraction}
                aria-label={PHASE[progress.phase]}
              />{" "}
              <span className="muted">{PHASE[progress.phase]}</span>
            </div>
          )}
        </form>
      )}
    </div>
  );
}
